import { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import swal from 'sweetalert';
import Sidebar_Progress from '../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../LoginHeaderOperational';
import { API_URL } from '../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../utils/platformAuth';
import {
  deleteMasterPlan,
  fetchMasterAdmins,
  fetchMasterPlans,
  fetchMasterSettings,
  saveMasterPlan,
  saveMasterSettings,
  updateMasterAdmin,
} from './adminApi';
import { INDIAN_GST_STATES } from '../../../constants/indianGstStates';
import MasterMessagingSettings from './MasterMessagingSettings.component';
import MasterCouponSettings from './MasterCouponSettings.component';
import './admin-dashboard.css';

const DEFAULT_COMPANY = {
  company_name: '',
  company_legal_name: '',
  company_billing_address: '',
  company_registered_address: '',
  company_phone: '',
  company_email: '',
  company_website: '',
  company_gst_no: '',
  company_pan_no: '',
  company_state_code: '',
  company_state_name: '',
  company_place_of_supply: '',
  invoice_bill_prefix: '',
  support_email: '',
  platform_tagline: '',
  default_invoice_gst_enabled: true,
  maintenance_mode: false,
  maintenance_message: '',
};

const EMPTY_PLAN = {
  plan_name: '',
  plan_desc: '',
  amount: '',
  discount: 0,
  duration: 30,
  max_nozzles: '',
  max_monthly_transactions: '',
  max_employees: '',
  max_tanks: '',
  advance_offers: [],
  sort_order: 0,
  status: 1,
  is_trial: false,
};

function formatAdvanceOffers(offers) {
  if (!Array.isArray(offers) || offers.length === 0) {
    return '—';
  }
  return offers
    .map((row) => `${row.months} mo: ${row.discount_percent}%`)
    .join(', ');
}

/** Empty / 0 → Unlimited for display. */
function formatPlanLimit(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? String(n) : 'Unlimited';
}

const AdminMasterSettings = (props) => {
  const history = useHistory();
  const [tab, setTab] = useState('company');
  const [company, setCompany] = useState(DEFAULT_COMPANY);
  const [plans, setPlans] = useState([]);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [adminForm, setAdminForm] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => {
        if (profile) loadAll();
        else history.goBack();
      })
      .catch(() => history.goBack());
  }, [history]);

  function loadAll() {
    setLoading(true);
    Promise.all([fetchMasterSettings(), fetchMasterPlans(), fetchMasterAdmins()])
      .then(([settingsRes, plansRes, adminsRes]) => {
        if (settingsRes.status) setCompany({ ...DEFAULT_COMPANY, ...settingsRes.data });
        if (plansRes.status) setPlans(plansRes.plans || []);
        if (adminsRes.status) {
          setAdmins(adminsRes.admins || []);
          const current = (adminsRes.admins || [])[0];
          if (current) {
            setAdminForm({
              id: current.id,
              name: current.name || '',
              user_name: current.user_name || '',
              email: current.email || '',
              phone: current.phone || '',
              password: '',
              current_password: '',
            });
          }
        }
      })
      .finally(() => setLoading(false));
  }

  function handleCompanyChange(name, value) {
    setCompany((prev) => ({ ...prev, [name]: value }));
  }

  function handleStateChange(code) {
    const state = INDIAN_GST_STATES.find((s) => s.code === code);
    setCompany((prev) => ({
      ...prev,
      company_state_code: code,
      company_state_name: state?.name || '',
      company_place_of_supply: prev.company_place_of_supply || state?.name || '',
    }));
  }

  function saveCompanySettings(e) {
    e.preventDefault();
    // Platform tab must not require company profile fields (otherwise maintenance toggle 422s).
    const payload = tab === 'platform'
      ? {
          invoice_bill_prefix: company.invoice_bill_prefix || '',
          support_email: company.support_email || '',
          platform_tagline: company.platform_tagline || '',
          default_invoice_gst_enabled: !!company.default_invoice_gst_enabled,
          maintenance_mode: !!company.maintenance_mode,
          maintenance_message: company.maintenance_message || '',
        }
      : company;

    saveMasterSettings(payload)
      .then((res) => {
        if (res.status) {
          if (res.data) setCompany((prev) => ({ ...prev, ...res.data }));
          swal('Saved', res.message, 'success');
        } else {
          swal('Error', res.message || 'Save failed', 'error');
        }
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function editPlan(plan) {
    setEditingPlanId(plan.id);
    setPlanForm({
      plan_name: plan.plan_name,
      plan_desc: plan.plan_desc || '',
      amount: plan.amount,
      discount: plan.discount || 0,
      duration: plan.duration,
      max_nozzles: plan.max_nozzles || '',
      max_monthly_transactions: plan.max_monthly_transactions || '',
      max_employees: plan.max_employees || '',
      max_tanks: plan.max_tanks || '',
      advance_offers: Array.isArray(plan.advance_offers) && plan.advance_offers.length
        ? plan.advance_offers.map((row) => ({
          months: row.months,
          discount_percent: row.discount_percent,
        }))
        : [],
      sort_order: plan.sort_order || 0,
      status: plan.status,
      is_trial: Boolean(plan.is_trial),
    });
  }

  function resetPlanForm() {
    setEditingPlanId(null);
    setPlanForm(EMPTY_PLAN);
  }

  function addAdvanceOffer() {
    setPlanForm((prev) => ({
      ...prev,
      advance_offers: [...(prev.advance_offers || []), { months: 12, discount_percent: 10 }],
    }));
  }

  function updateAdvanceOffer(index, field, value) {
    setPlanForm((prev) => {
      const next = [...(prev.advance_offers || [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, advance_offers: next };
    });
  }

  function removeAdvanceOffer(index) {
    setPlanForm((prev) => ({
      ...prev,
      advance_offers: (prev.advance_offers || []).filter((_, i) => i !== index),
    }));
  }

  function savePlan(e) {
    e.preventDefault();
    saveMasterPlan(
      {
        ...planForm,
        discount: Number(planForm.discount || 0),
        duration: Number(planForm.duration),
        sort_order: Number(planForm.sort_order || 0),
        status: Number(planForm.status),
        is_trial: Boolean(planForm.is_trial),
        amount: planForm.is_trial ? 0 : Number(planForm.amount),
        advance_offers: (planForm.advance_offers || [])
          .map((row) => ({
            months: Number(row.months),
            discount_percent: Number(row.discount_percent),
          }))
          .filter((row) => row.months >= 1 && row.discount_percent >= 0),
      },
      editingPlanId
    )
      .then((res) => {
        if (res.status) {
          swal('Saved', res.message, 'success');
          resetPlanForm();
          fetchMasterPlans().then((r) => setPlans(r.plans || []));
        } else {
          swal('Error', res.message || 'Save failed', 'error');
        }
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function deactivatePlan(planId) {
    swal({ title: 'Deactivate this plan?', icon: 'warning', buttons: true }).then((ok) => {
      if (!ok) return;
      deleteMasterPlan(planId).then((res) => {
        if (res.status) {
          swal('Done', res.message, 'success');
          fetchMasterPlans().then((r) => setPlans(r.plans || []));
        }
      });
    });
  }

  function saveAdminAccount(e) {
    e.preventDefault();
    if (!adminForm.id) return;
    updateMasterAdmin(adminForm.id, adminForm)
      .then((res) => {
        if (res.status) {
          swal('Saved', res.message, 'success');
          setAdminForm((prev) => ({ ...prev, password: '', current_password: '' }));
          fetchMasterAdmins().then((r) => setAdmins(r.admins || []));
        } else {
          swal('Error', res.message || 'Update failed', 'error');
        }
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function selectAdmin(admin) {
    setAdminForm({
      id: admin.id,
      name: admin.name || '',
      user_name: admin.user_name || '',
      email: admin.email || '',
      phone: admin.phone || '',
      password: '',
      current_password: '',
    });
  }

  return (
    <div className="main-container page-wrap admin-platform">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={29} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Master Settings" />
          <div className="container pb-3 mt-3">
            <Link to="/admin_dashboard" className="small">← Control Center</Link>

            <ul className="nav nav-tabs mt-3 mb-3">
              {[
                ['company', 'Company & billing'],
                ['plans', 'Subscription plans'],
                ['coupons', 'Coupons'],
                ['admin', 'Admin accounts'],
                ['messaging', 'Messaging'],
                ['platform', 'Platform config'],
              ].map(([key, label]) => (
                <li className="nav-item" key={key}>
                  <button
                    type="button"
                    className={`nav-link ${tab === key ? 'active' : ''}`}
                    onClick={() => setTab(key)}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>

            {loading ? (
              <p className="text-muted">Loading master settings...</p>
            ) : (
              <>
                {(tab === 'company' || tab === 'platform') && (
                  <form onSubmit={saveCompanySettings} className="admin-panel col-lg-10">
                    <h5>{tab === 'company' ? 'Company profile & billing address' : 'Platform configuration'}</h5>
                    <p className="text-muted small">
                      Used on Petrosmart invoices to clients (pumps). Billing address, GSTIN, PAN, and state code appear as seller details on printed subscription and service invoices.
                    </p>

                    {tab === 'company' && (
                      <>
                        <div className="row g-2">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Company name *</label>
                            <input className="form-control" value={company.company_name} onChange={(e) => handleCompanyChange('company_name', e.target.value)} required />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Legal name</label>
                            <input className="form-control" value={company.company_legal_name} onChange={(e) => handleCompanyChange('company_legal_name', e.target.value)} />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Billing address *</label>
                          <textarea className="form-control" rows={3} value={company.company_billing_address} onChange={(e) => handleCompanyChange('company_billing_address', e.target.value)} required />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Registered address</label>
                          <textarea className="form-control" rows={2} value={company.company_registered_address} onChange={(e) => handleCompanyChange('company_registered_address', e.target.value)} />
                        </div>
                        <div className="row g-2">
                          <div className="col-md-4 mb-3">
                            <label className="form-label">State</label>
                            <select className="form-select" value={company.company_state_code} onChange={(e) => handleStateChange(e.target.value)}>
                              <option value="">Select state...</option>
                              {INDIAN_GST_STATES.map((s) => (
                                <option key={s.code} value={s.code}>{s.code} - {s.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label">Place of supply</label>
                            <input className="form-control" value={company.company_place_of_supply} onChange={(e) => handleCompanyChange('company_place_of_supply', e.target.value)} />
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label">Phone</label>
                            <input className="form-control" value={company.company_phone} onChange={(e) => handleCompanyChange('company_phone', e.target.value)} />
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label">Email</label>
                            <input type="email" className="form-control" value={company.company_email} onChange={(e) => handleCompanyChange('company_email', e.target.value)} />
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label">Website</label>
                            <input className="form-control" value={company.company_website} onChange={(e) => handleCompanyChange('company_website', e.target.value)} />
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label">GST number</label>
                            <input className="form-control" value={company.company_gst_no} onChange={(e) => handleCompanyChange('company_gst_no', e.target.value.toUpperCase())} />
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label">PAN</label>
                            <input className="form-control" value={company.company_pan_no} onChange={(e) => handleCompanyChange('company_pan_no', e.target.value.toUpperCase())} />
                          </div>
                        </div>
                      </>
                    )}

                    {tab === 'platform' && (
                      <div className="row g-2">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Invoice bill prefix</label>
                          <input className="form-control" value={company.invoice_bill_prefix} onChange={(e) => handleCompanyChange('invoice_bill_prefix', e.target.value)} placeholder="e.g. PS-" />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Support email</label>
                          <input type="email" className="form-control" value={company.support_email} onChange={(e) => handleCompanyChange('support_email', e.target.value)} />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Platform tagline</label>
                          <input className="form-control" value={company.platform_tagline} onChange={(e) => handleCompanyChange('platform_tagline', e.target.value)} />
                        </div>
                        <div className="col-md-12 mb-3">
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={company.default_invoice_gst_enabled !== false}
                              onChange={(e) => handleCompanyChange('default_invoice_gst_enabled', e.target.checked)}
                            />
                            <label className="form-check-label">Default GST invoicing for Petrosmart client bills</label>
                          </div>
                        </div>

                        <hr className="my-4" />
                        <h6 className="mb-2">Maintenance mode (all pumps)</h6>
                        <p className="text-muted small">
                          Enable before migration or platform downtime. All pump users are blocked from login and data entry.
                          Super admin access stays available so you can turn this off.
                        </p>
                        <div className="col-md-12 mb-3">
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="global_maintenance_mode"
                              checked={!!company.maintenance_mode}
                              onChange={(e) => handleCompanyChange('maintenance_mode', e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="global_maintenance_mode">
                              Enable maintenance mode for <strong>all pumps</strong>
                            </label>
                          </div>
                        </div>
                        <div className="col-md-12 mb-3">
                          <label className="form-label">Client-facing message</label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={company.maintenance_message || ''}
                            onChange={(e) => handleCompanyChange('maintenance_message', e.target.value)}
                            placeholder="System is under maintenance. Please try again later."
                          />
                        </div>
                      </div>
                    )}

                    <button type="submit" className="btn btn-primary">Save settings</button>
                  </form>
                )}

                {tab === 'plans' && (
                  <div className="row">
                    <div className="col-lg-7">
                      <div className="admin-panel">
                        <h5>Subscription plan packages</h5>
                        <div className="table-responsive">
                          <table className="table table-sm table-bordered">
                            <thead className="table-light">
                              <tr>
                                <th>Name</th>
                                <th>Amount</th>
                                <th>Days</th>
                                <th>Nozzles</th>
                                <th>Monthly txns</th>
                                <th>Employees</th>
                                <th>Tanks</th>
                                <th>Advance offers</th>
                                <th>Status</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {plans.map((plan) => (
                                <tr key={plan.id}>
                                  <td>
                                    <strong>{plan.plan_name}</strong>
                                    {plan.is_trial ? <span className="badge bg-success ms-2">Trial</span> : null}
                                    <div className="small text-muted">{plan.plan_desc}</div>
                                  </td>
                                  <td>₹{Number(plan.amount).toFixed(2)}</td>
                                  <td>{plan.duration}</td>
                                  <td>{formatPlanLimit(plan.max_nozzles)}</td>
                                  <td>{formatPlanLimit(plan.max_monthly_transactions)}</td>
                                  <td>{formatPlanLimit(plan.max_employees)}</td>
                                  <td>{formatPlanLimit(plan.max_tanks)}</td>
                                  <td className="small">{formatAdvanceOffers(plan.advance_offers)}</td>
                                  <td>{plan.status === 1 ? 'Active' : 'Inactive'}</td>
                                  <td className="text-nowrap">
                                    <button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => editPlan(plan)}>Edit</button>
                                    {plan.status === 1 && (
                                      <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => deactivatePlan(plan.id)}>Deactivate</button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-5">
                      <form onSubmit={savePlan} className="admin-panel border rounded p-3">
                        <h6>{editingPlanId ? 'Edit plan' : 'Add plan'}</h6>
                        <div className="mb-2">
                          <label className="form-label">Plan name</label>
                          <input className="form-control" value={planForm.plan_name} onChange={(e) => setPlanForm({ ...planForm, plan_name: e.target.value })} required />
                        </div>
                        <div className="mb-2">
                          <label className="form-label">Description</label>
                          <textarea className="form-control" rows={2} value={planForm.plan_desc} onChange={(e) => setPlanForm({ ...planForm, plan_desc: e.target.value })} />
                        </div>
                        <div className="row g-2">
                          <div className="col-6 mb-2">
                            <label className="form-label">Amount (₹)</label>
                            <input type="number" className="form-control" value={planForm.amount} onChange={(e) => setPlanForm({ ...planForm, amount: e.target.value })} required disabled={Boolean(planForm.is_trial)} />
                          </div>
                          <div className="col-6 mb-2">
                            <label className="form-label">Discount (₹)</label>
                            <input type="number" className="form-control" value={planForm.discount} onChange={(e) => setPlanForm({ ...planForm, discount: e.target.value })} />
                          </div>
                          <div className="col-6 mb-2">
                            <label className="form-label">Duration (days)</label>
                            <input type="number" className="form-control" value={planForm.duration} onChange={(e) => setPlanForm({ ...planForm, duration: e.target.value })} required />
                          </div>
                          <div className="col-12 mb-2">
                            <label className="d-flex align-items-center gap-2 mb-0">
                              <input
                                type="checkbox"
                                checked={Boolean(planForm.is_trial)}
                                onChange={(e) => setPlanForm({
                                  ...planForm,
                                  is_trial: e.target.checked,
                                  amount: e.target.checked ? 0 : planForm.amount,
                                })}
                              />
                              <span className="form-label mb-0">Free trial (₹0, no Razorpay)</span>
                            </label>
                          </div>
                          <div className="col-6 mb-2">
                            <label className="form-label">Sort order</label>
                            <input type="number" className="form-control" value={planForm.sort_order} onChange={(e) => setPlanForm({ ...planForm, sort_order: e.target.value })} />
                          </div>
                          <div className="col-6 mb-2">
                            <label className="form-label">Max nozzles</label>
                            <input type="number" min="0" className="form-control" value={planForm.max_nozzles} onChange={(e) => setPlanForm({ ...planForm, max_nozzles: e.target.value })} placeholder="Blank = unlimited" />
                          </div>
                          <div className="col-6 mb-2">
                            <label className="form-label">Max monthly transactions</label>
                            <input type="number" min="0" className="form-control" value={planForm.max_monthly_transactions} onChange={(e) => setPlanForm({ ...planForm, max_monthly_transactions: e.target.value })} placeholder="Blank = unlimited" />
                          </div>
                          <div className="col-6 mb-2">
                            <label className="form-label">Max employees</label>
                            <input type="number" min="0" className="form-control" value={planForm.max_employees} onChange={(e) => setPlanForm({ ...planForm, max_employees: e.target.value })} placeholder="Blank = unlimited" />
                          </div>
                          <div className="col-6 mb-2">
                            <label className="form-label">Max tanks</label>
                            <input type="number" min="0" className="form-control" value={planForm.max_tanks} onChange={(e) => setPlanForm({ ...planForm, max_tanks: e.target.value })} placeholder="Blank = unlimited" />
                          </div>
                        </div>
                        <p className="small text-muted mb-3">Leave limit fields blank (or 0) for unlimited. Amount is the monthly price used when a pump buys N months online.</p>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label mb-0">Advance payment offers</label>
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={addAdvanceOffer}>
                              Add offer
                            </button>
                          </div>
                          <p className="small text-muted mb-2">
                            Example: 12 months with 10% off. The best matching offer is applied when the customer pays that many months (or more) in advance.
                          </p>
                          {(planForm.advance_offers || []).length === 0 && (
                            <div className="small text-muted mb-2">No advance offers. Customers can still buy 1–36 months at the full monthly rate.</div>
                          )}
                          {(planForm.advance_offers || []).map((offer, index) => (
                            <div className="row g-2 align-items-end mb-2" key={`offer-${index}`}>
                              <div className="col-5">
                                <label className="form-label small">Months</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="36"
                                  className="form-control"
                                  value={offer.months}
                                  onChange={(e) => updateAdvanceOffer(index, 'months', e.target.value)}
                                />
                              </div>
                              <div className="col-5">
                                <label className="form-label small">Discount %</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  className="form-control"
                                  value={offer.discount_percent}
                                  onChange={(e) => updateAdvanceOffer(index, 'discount_percent', e.target.value)}
                                />
                              </div>
                              <div className="col-2">
                                <button type="button" className="btn btn-outline-danger w-100" onClick={() => removeAdvanceOffer(index)}>
                                  ×
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <select className="form-select" value={planForm.status} onChange={(e) => setPlanForm({ ...planForm, status: e.target.value })}>
                            <option value={1}>Active</option>
                            <option value={0}>Inactive</option>
                          </select>
                        </div>
                        <button type="submit" className="btn btn-primary me-2">{editingPlanId ? 'Update plan' : 'Add plan'}</button>
                        {editingPlanId && (
                          <button type="button" className="btn btn-outline-secondary" onClick={resetPlanForm}>Cancel</button>
                        )}
                      </form>
                    </div>
                  </div>
                )}

                {tab === 'coupons' && <MasterCouponSettings />}

                {tab === 'messaging' && <MasterMessagingSettings />}

                {tab === 'admin' && (
                  <div className="row">
                    <div className="col-md-4">
                      <div className="admin-panel">
                        <h6>Super admin accounts</h6>
                        <ul className="list-group list-group-flush">
                          {admins.map((admin) => (
                            <button
                              key={admin.id}
                              type="button"
                              className={`list-group-item list-group-item-action ${adminForm.id === admin.id ? 'active' : ''}`}
                              onClick={() => selectAdmin(admin)}
                            >
                              {admin.name || admin.user_name}
                              <div className="small">{admin.user_name}</div>
                            </button>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="col-md-8">
                      <form onSubmit={saveAdminAccount} className="admin-panel border rounded p-3">
                        <h6>Update admin credentials</h6>
                        <p className="small text-muted">Enter your current password to change username or password.</p>
                        <div className="row g-2">
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Display name</label>
                            <input className="form-control" value={adminForm.name || ''} onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Login username</label>
                            <input className="form-control" value={adminForm.user_name || ''} onChange={(e) => setAdminForm({ ...adminForm, user_name: e.target.value })} />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Email</label>
                            <input type="email" className="form-control" value={adminForm.email || ''} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Phone</label>
                            <input className="form-control" value={adminForm.phone || ''} onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })} />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">New password</label>
                            <input type="password" className="form-control" value={adminForm.password || ''} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} placeholder="Leave blank to keep unchanged" />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Current password *</label>
                            <input type="password" className="form-control" value={adminForm.current_password || ''} onChange={(e) => setAdminForm({ ...adminForm, current_password: e.target.value })} required={!!adminForm.password || adminForm.user_name !== admins.find((a) => a.id === adminForm.id)?.user_name} />
                          </div>
                        </div>
                        <button type="submit" className="btn btn-primary mt-2">Save admin account</button>
                      </form>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMasterSettings;
